import { useEffect, useState } from "react";

import { blobDownloadURL } from "_/data/blob";

import { type TelemetryFile } from ".";

/**
 * The wasm and worker files are copied into the build by `CopyPlugin`.
 * `output.publicPath` is unset, so these URLs are absolute.
 */
const DUCKDB_BUNDLES = {
  mvp: {
    mainModule: "/static/duckdb/duckdb-mvp.wasm",
    mainWorker: "/static/duckdb/duckdb-browser-mvp.worker.js",
  },
  eh: {
    mainModule: "/static/duckdb/duckdb-eh.wasm",
    mainWorker: "/static/duckdb/duckdb-browser-eh.worker.js",
  },
};

/**
 * Name the file is registered under in DuckDB's virtual filesystem. A short
 * name keeps the view's SQL readable when an error surfaces it.
 */
export const TELEMETRY_FILE_NAME = "telemetry.parquet";

/** SQL creating the one view the console queries. */
export function telemetryViewSql(fileName: string): string {
  return `CREATE OR REPLACE VIEW telemetry AS SELECT * FROM read_parquet('${fileName}');`;
}

/**
 * An Arrow result row. `Table#toArray` is declared `any[]` by apache-arrow, so
 * accepting it through this shape confines the untyped boundary to `resultRows`
 * instead of repeating it at every call site.
 */
type ArrowResult = {
  toArray: () => { toJSON: () => Record<string, unknown> }[];
};

/** Read an Arrow query result as plain objects. */
function resultRows(result: ArrowResult): Record<string, unknown>[] {
  return result.toArray().map((row) => row.toJSON());
}

/** One column of the `telemetry` view, as reported by `DESCRIBE`. */
export type TelemetryColumn = {
  name: string;
  type: string;
};

/** One distinct `kind` present in an attempt's telemetry, with its value type. */
export type TelemetryKind = {
  kind: string;
  valueType: string;
};

export type TelemetryQueryResult = {
  columns: string[];
  rows: Record<string, unknown>[];
};

export type TelemetryDatabase = {
  query: (sql: string) => Promise<TelemetryQueryResult>;
  viewColumns: TelemetryColumn[];
  viewKinds: TelemetryKind[];
  close: () => Promise<void>;
};

/**
 * Download an attempt's telemetry file and expose it as a queryable view.
 *
 * The file comes through our own blob route, so the request is same-origin and
 * carries the session cookie. It is downloaded in full.
 */
async function createTelemetryDatabase(
  file: TelemetryFile
): Promise<TelemetryDatabase> {
  // Imported dynamically so webpack keeps the multi-megabyte DuckDB chunk out
  // of the main bundle.
  const duckdb = await import("@duckdb/duckdb-wasm");

  const bundle = await duckdb.selectBundle(DUCKDB_BUNDLES);
  if (!bundle.mainWorker) {
    throw new Error("No supported DuckDB build for this browser.");
  }

  const worker = new Worker(bundle.mainWorker);
  const logger = new duckdb.ConsoleLogger(duckdb.LogLevel.WARNING);
  const db = new duckdb.AsyncDuckDB(logger, worker);

  // Hoisted so the `catch` below can close it, rather than leaking the Worker
  // and its wasm heap.
  let connection: Awaited<ReturnType<typeof db.connect>> | undefined;

  try {
    await db.instantiate(bundle.mainModule, bundle.pthreadWorker);

    const response = await fetch(blobDownloadURL(file.blobId));
    if (!response.ok) {
      throw new Error(`Could not download telemetry: ${response.status}`);
    }

    await db.registerFileBuffer(
      TELEMETRY_FILE_NAME,
      new Uint8Array(await response.arrayBuffer())
    );

    connection = await db.connect();
    await connection.query(telemetryViewSql(TELEMETRY_FILE_NAME));

    const described = await connection.query("DESCRIBE telemetry;");
    const viewColumns = resultRows(described).map((row) => ({
      name: String(row.column_name),
      type: String(row.column_type),
    }));

    // Which kinds an attempt recorded is not visible in the schema, and a
    // kind's value type tells you which `value_*` column to read.
    const kinds = await connection.query(
      "SELECT DISTINCT kind, value_type FROM telemetry ORDER BY kind, value_type;"
    );
    const viewKinds = resultRows(kinds).map((row) => ({
      kind: String(row.kind),
      valueType: String(row.value_type),
    }));

    // Narrowed to a `const` so the closures below keep the non-optional type.
    const openConnection = connection;

    return {
      viewColumns,
      viewKinds,

      query: async (sql) => {
        const result = await openConnection.query(sql);

        return {
          columns: result.schema.fields.map((field) => field.name),
          rows: resultRows(result),
        };
      },

      close: async () => {
        try {
          await openConnection.close();
        } finally {
          await db.terminate();
        }
      },
    };
  } catch (cause) {
    if (connection) {
      await connection.close().catch(() => undefined);
    }
    await db.terminate();
    throw cause;
  }
}

export type TelemetryDatabaseState =
  | { status: "loading" }
  | {
      status: "ready";
      query: (sql: string) => Promise<TelemetryQueryResult>;
      viewColumns: TelemetryColumn[];
      viewKinds: TelemetryKind[];
    }
  | { status: "error"; error: Error };

/**
 * Close a database, discarding any failure.
 *
 * A close error cannot be shown to anyone: it happens after the caller moved on,
 * or while unmounting.
 */
function closeQuietly(database: TelemetryDatabase): void {
  void database.close().catch(() => undefined);
}

/**
 * Create a telemetry database for a file, tearing it down when the file changes
 * or the caller unmounts.
 *
 * The database owns a Web Worker and several megabytes of registered file
 * buffers, so it is built and torn down in an effect.
 */
export function useTelemetryDatabase(
  file: TelemetryFile | null | undefined
): TelemetryDatabaseState {
  const [state, setState] = useState<TelemetryDatabaseState>({
    status: "loading",
  });

  useEffect(() => {
    if (!file) {
      // Reset, or a file going back to absent leaves a stale `ready` state
      // pointing at the connection closed below.
      setState({ status: "loading" });
      return undefined;
    }

    let cancelled = false;
    let database: TelemetryDatabase | undefined;

    setState({ status: "loading" });

    const load = async () => {
      try {
        const created = await createTelemetryDatabase(file);
        database = created;

        if (cancelled) {
          closeQuietly(created);
          return;
        }

        setState({
          status: "ready",
          query: created.query,
          viewColumns: created.viewColumns,
          viewKinds: created.viewKinds,
        });
      } catch (cause) {
        if (cancelled) return;
        setState({
          status: "error",
          error: cause instanceof Error ? cause : new Error(String(cause)),
        });
      }
    };

    void load();

    return () => {
      cancelled = true;
      if (database) closeQuietly(database);
    };
  }, [file]);

  return state;
}
